import { Row, Col, Statistic, Spin } from 'antd';
import {
  FileTextOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  SafetyOutlined,
  UserOutlined,
  BankOutlined,
  ToolOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import type { DashboardMetricsResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { WisecureCard } from '@/components/base';
import type { PaginatedResponse, DashboardSocietyListItem } from '@/types/dashboard';
import { neutral } from '@/theme';

export function HomePage() {
  const { data: stats, isLoading } = useWisecureQuery<DashboardMetricsResponse>(
    queryKeys.dashboardMetrics,
    (client) => client.dashboard.dashboardControllerGetMetrics({}),
  );

  const { data: securitySocs, isLoading: loadingSecurity } = useWisecureQuery<
    PaginatedResponse<DashboardSocietyListItem>
  >(
    ['dashboard-societies', 'SECURITY'],
    (client) =>
      client.dashboard.dashboardControllerListSocieties({ type: 'SECURITY', limit: 1 }) as Promise<
        PaginatedResponse<DashboardSocietyListItem>
      >,
  );

  const { data: installerSocs, isLoading: loadingInstallers } = useWisecureQuery<
    PaginatedResponse<DashboardSocietyListItem>
  >(
    ['dashboard-societies', 'INSTALLER'],
    (client) =>
      client.dashboard.dashboardControllerListSocieties({ type: 'INSTALLER', limit: 1 }) as Promise<
        PaginatedResponse<DashboardSocietyListItem>
      >,
  );

  const { data: distributorSocs, isLoading: loadingDistributors } = useWisecureQuery<
    PaginatedResponse<DashboardSocietyListItem>
  >(
    ['dashboard-societies', 'DISTRIBUTOR'],
    (client) =>
      client.dashboard.dashboardControllerListSocieties({ type: 'DISTRIBUTOR', limit: 1 }) as Promise<
        PaginatedResponse<DashboardSocietyListItem>
      >,
  );

  const loading = isLoading || loadingSecurity || loadingInstallers || loadingDistributors;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Accueil" subtitle="Vue d'ensemble du tableau de bord" />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <WisecureCard dsVariant="highlight">
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.65)' }}>Documents agent à valider</span>}
              value={stats?.pendingDocuments ?? 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: neutral[0] }}
            />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard dsVariant="highlight">
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.65)' }}>Documents société à valider</span>}
              value={stats?.pendingSocieties ?? 0}
              prefix={<ShopOutlined />}
              valueStyle={{ color: neutral[0] }}
            />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard dsVariant="highlight">
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.65)' }}>Documents en attente</span>}
              value={stats?.pendingDocuments ?? 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: neutral[0] }}
            />
          </WisecureCard>
        </Col>

        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic title="Clients inscrits" value={stats?.totalClients ?? 0} prefix={<TeamOutlined />} />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic title="Agents inscrits" value={stats?.totalAgents ?? 0} prefix={<SafetyOutlined />} />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic title="Patrons inscrits" value={stats?.totalSocietyOwners ?? 0} prefix={<UserOutlined />} />
          </WisecureCard>
        </Col>

        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic
              title="Sociétés de sécurité"
              value={securitySocs?.total ?? 0}
              prefix={<BankOutlined />}
            />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic
              title="Installateurs"
              value={installerSocs?.total ?? 0}
              prefix={<ToolOutlined />}
            />
          </WisecureCard>
        </Col>
        <Col xs={24} sm={8}>
          <WisecureCard>
            <Statistic
              title="Distributeurs"
              value={distributorSocs?.total ?? 0}
              prefix={<InboxOutlined />}
            />
          </WisecureCard>
        </Col>
      </Row>
    </div>
  );
}
